import React, { useState, useRef, useEffect } from "react";
import Logo from "../assets/images/UT_Horz_Yellow.svg";
import AuditorLogo from "../assets/images/auditor-logo-w-text-light.svg";

/* Types */

interface MenuItem {
  label: string;
  href: string;
  desc: string;
  icon: string;
  badge?: string;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

interface MenuCategory {
  label: string;
  sections: MenuSection[];
}

/* Menu Data */

const navData: Record<string, MenuCategory> = {
  compensation: {
    label: "Compensation",
    sections: [
      {
        title: "Employee Compensation",
        items: [
          { label: "Employee Pay Search", href: "/employees/employee-search", desc: "Look up compensation by name or agency", icon: "search" },
          { label: "Highest Paid Employees", href: "/employees/highest-paid-employees", desc: "Top earners across state & local government", icon: "trophy" },
          { label: "Compensation Downloader", href: "/entities/compensation-downloader", desc: "Export bulk compensation datasets", icon: "download" },
        ],
      },
      {
        title: "Job Title",
        items: [
          { label: "Job Pay Search", href: "/employees/job-title-search", desc: "Find salary ranges by job title", icon: "briefcase" },
          { label: "Compare Two Entities", href: "entities/compare-two-entities", desc: "Side-by-side pay comparison tool", icon: "scale" },
          { label: "Wage Trends", href: "/employees/job-title-wage-trends", desc: "Track pay changes over time", icon: "trending" },
          { label: "Highest Paid Jobs", href: "/employees/highest-paid-jobs", desc: "Top-paying positions in government", icon: "star" },
          { label: "Most Common Jobs", href: "/employees/most-common-jobs", desc: "Frequently held government positions", icon: "users" },
          { label: "Local Gov. Jobs", href: "/employees/local-government-jobs", desc: "Positions in city & county agencies", icon: "building" },
        ],
      },
    ],
  },
  governments: {
    label: "Governments",
    sections: [
      {
        title: "Financial Tools",
        items: [
          { label: "Highest Paid Vendors", href: "/entities/highest-paid-vendors", desc: "Examine an entity's highest paid vendors", icon: "trophy" },
          { label: "Vendor Payments Search", href: "/entities/vendor-payments-search", desc: "Search for a vendor's payments", icon: "search" },
          { label: "Entity Overview", href: "/entities/entity-overview-dashboard", desc: "Revenue and expenditure dashboard", icon: "landmark" },
          { label: "Cash and Investments", href: "https://transparent.utah.gov/gai-dashboard//", desc: "Governmental entity cash reserves, investments, and assets", icon: "landmark" },
          { label: "Transaction Details", href: "https://old.transparent.utah.gov/transaction_details.php", desc: "Inspect an entity's transactions", icon: "file" },
          { label: "CARES Dashboard", href: "https://old.transparent.utah.gov/cares_dashboard.php", desc: "Explore CARES Act funding data", icon: "chart" },
          { label: "ARPA Dashboard", href: "https://old.transparent.utah.gov/arpa_dashboard.php", desc: "Explore ARPA funding data", icon: "chart" },
        ],
      },
      {
        title: "Reporting & Oversight",
        items: [
          { label: "Local Gov. Reporting", href: "https://reporting.auditor.utah.gov/", desc: "Local government reporting system", icon: "building" },
          { label: "Search Financial Reports", href: "https://reporting.auditor.utah.gov/searchreports/s/", desc: "Search submitted financial reports", icon: "search" },
          { label: "Compliance Report", href: "https://old.transparent.utah.gov/compliance.php", desc: "Entity compliance status", icon: "shield" },
          { label: "Submission List", href: "https://old.transparent.utah.gov/batches.php", desc: "View data submission records", icon: "list" },
          { label: "Fiscal Distress Dashboard", href: "/entities/fiscal-distress-dashboard", desc: "Analyze fiscal distress indicators", icon: "alert" },
          { label: "Data Quality Dashboard", href: "https://old.transparent.utah.gov/dqi_dashboard.php", desc: "Examine submitted data quality", icon: "check" },
          { label: "MIDA Public Timeline", href: "https://transparent.utah.gov/mida/", desc: "Explore MIDA", icon: "shield" },
        ],
      },
    ],
  },
  education: {
    label: "Education",
    sections: [
      {
        title: "Higher Education",
        items: [
          { label: "Higher Ed Expenditure", href: "/higher-education/higher-education-expenditure", desc: "How public funds are appropriated", icon: "graduation" },
          { label: "Higher Education – Cash", href: "https://transparent.utah.gov//he-cash/", desc: "Cash equivalents & cash flows", icon: "dollar" },
        ],
      },
      {
        title: "Public Education (K-12)",
        items: [
          { label: "Performance & Spending", href: "https://transparent.utah.gov/spending-map/", desc: "Compare spending and outcomes", icon: "chart" },
          { label: "Teacher Pay", href: "https://transparent.utah.gov/teacher-pay/", desc: "Trends in teacher compensation", icon: "users" },
          { label: "Admin & Teacher Pay", href: "https://transparent.utah.gov/lea-pay/", desc: "Compare admin vs. teacher pay", icon: "scale" },
          { label: "Education Funding", href: "https://transparent.utah.gov/lea-revenue/", desc: "How Utah schools are funded", icon: "dollar" },
          { label: "Entity Overview", href: "/entities/entity-overview-dashboard", desc: "LEA revenue & expenditure", icon: "landmark" },
          { label: "Title I Effectiveness", href: "https://old.transparent.utah.gov/pkids_title_i.php", desc: "Title I spending performance", icon: "file" },
        ],
      },
    ],
  },
  reports: {
    label: "Curated Reports",
    sections: [
      {
        title: "Taxpayer / Public Interest",
        items: [
          { label: "State Revenues & Expenditures", href: "https://old.transparent.utah.gov/state_rvex.php", desc: "20-year deep dive into state finances", icon: "trending", badge: "New" },
          { label: "Specialty License Plates", href: "https://transparent.utah.gov/specialty-license-report/", desc: "Animated license plate trend data", icon: "star", badge: "New" },
          { label: "Property Values", href: "https://old.transparent.utah.gov/property.php", desc: "Property assessments across Utah", icon: "building" },
        ],
      },
      {
        title: "Public Health Data",
        items: [
          { label: "Health Cost Compare", href: "https://healthcost.utah.gov/", desc: "Compare healthcare procedure prices", icon: "heart" },
          { label: "Medical Cannabis Visit Cost", href: "https://healthcost.utah.gov/cannabis.php", desc: "Compare medical cannabis provider prices", icon: "heart" },
        ],
      },
    ],
  },
  help: {
    label: "Help",
    sections: [
      {
        title: "Site Information",
        items: [
          { label: "Help", href: "/help", desc: "Frequently asked questions", icon: "help" },
          { label: "About", href: "https://old.transparent.utah.gov/about.php", desc: "About Transparent Utah", icon: "info" },
          { label: "Resources", href: "https://old.transparent.utah.gov/resources.php", desc: "Additional tools & links", icon: "link" },
          { label: "Disclaimers", href: "https://old.transparent.utah.gov/disclaimers.php", desc: "Legal disclaimers", icon: "file" },
        ],
      },
      {
        title: "Super Users",
        items: [
          { label: "Resource Center", href: "https://old.transparent.utah.gov/superuser.php", desc: "Super user tools and data", icon: "star" },
          { label: "Become a Super User", href: "https://old.transparent.utah.gov/how_to_become_superuser.php", desc: "Learn how to get advanced access", icon: "users" },
          { label: "Available Fields", href: "https://old.transparent.utah.gov/database_schema.php", desc: "Database schema & field reference", icon: "list" },
          { label: "Example SQL Scripts", href: "https://old.transparent.utah.gov/superuser_scripts.php", desc: "Sample queries to get started", icon: "code" },
          { label: "Query With Python/R", href: "https://old.transparent.utah.gov/superuser_query_with_r.php", desc: "Access data programmatically", icon: "code" },
        ],
      },
    ],
  },
};

/* SVG Icons */

const iconPaths: Record<string, React.ReactNode> = {
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </>
  ),
  trophy: <path d="M6 9H4a2 2 0 01-2-2V5a2 2 0 012-2h2M18 9h2a2 2 0 002-2V5a2 2 0 00-2-2h-2M9 22h6M12 17v5M8 3h8v7a4 4 0 11-8 0V3z" />,
  download: <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />,
  briefcase: (
    <>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
    </>
  ),
  scale: <path d="M12 3v18M3 7l3 9h6l3-9M15 7l3 9h3" />,
  trending: (
    <>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </>
  ),
  star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
  users: (
    <>
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </>
  ),
  building: (
    <>
      <rect x="4" y="2" width="16" height="20" rx="1" />
      <path d="M9 22V12h6v10M8 6h.01M16 6h.01M8 10h.01M16 10h.01" />
    </>
  ),
  landmark: <path d="M3 21h18M3 10h18M12 3l9 7H3l9-7zM5 10v11M19 10v11M9 10v11M15 10v11" />,
  graduation: <path d="M22 10l-10-5L2 10l10 5 10-5zM6 12v5c0 2 3 3 6 3s6-1 6-3v-5" />,
  file: (
    <>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </>
  ),
  chart: <path d="M18 20V10M12 20V4M6 20v-6" />,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  list: (
    <>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </>
  ),
  alert: (
    <>
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  check: (
    <>
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </>
  ),
  dollar: (
    <>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </>
  ),
  code: (
    <>
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </>
  ),
  heart: <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />,
};

const Icon = ({ name, size = 18 }: { name: string; size?: number }) => (
  <svg
    className="tu-megaIcon__svg"
    width={size}
    height={size}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {iconPaths[name] || iconPaths.file}
  </svg>
);

const MegaPanel = ({ sections }: { sections: MenuSection[] }) => (
  <div className="tu-megaPanel">
    {sections.map((section, si) => (
      <div className="tu-megaPanel__section" key={si}>
        <div className="tu-megaPanel__sectionTitle">{section.title}</div>
        {section.items.map((item, i) => (
          <a
            key={i}
            className="tu-megaPanel__link"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="tu-megaPanel__icon">
              <Icon name={item.icon} />
            </span>
            <span>
              <span className="tu-megaPanel__label">
                {item.label}
                {item.badge && <span className="tu-megaPanel__badge">{item.badge}</span>}
              </span>
              <span className="tu-megaPanel__desc">{item.desc}</span>
            </span>
          </a>
        ))}
      </div>
    ))}
  </div>
);

const Chevron = () => (
  <svg
    className="tu-megaNav__chevron"
    width="14"
    height="14"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Hamburger = () => (
  <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);


/* Utah State Flag SVG */

const UtahFlag = () => (
  <svg
    className="tu-govBanner__flag"
    viewBox="0 0 190.5 114.3"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Utah state flag"
    role="img"
    focusable="false"
  >
    <path d="m-.0000007 0h190.5000007v114.3h-190.5000007z" fill="#fff" strokeWidth="1.41455" />
    <path d="m190.5 0v46.153916l-19.05-11.001374-9.525 5.503332-28.575-16.499416-9.5197 5.498042-28.580301-16.499416-28.580299 16.499416-9.5197-5.498042-28.575 16.499416-9.525-5.503332-19.0499993 11.001374v-46.153916z" fill="#071d49" strokeWidth=".529166" />
    <path d="m128.24619 76.2v-38.1l-32.993541-19.05-32.998839 19.05v38.1l32.998839 19.05z" fill="#071d49" strokeWidth=".529166" />
    <g fill="#ffb81d" transform="scale(.52916665)">
      <path d="m180 42.53-56.7 32.74v65.47l56.7 32.74 56.7-32.74v-65.47zm54 96.65-54 31.18-54-31.18v-62.35l54-31.18 54 31.18z" />
      <path d="m199.53 75.56h-39.03c-.75 0-1.13-.92-.59-1.44 5.68-5.44 12.56-8.98 20.08-8.98s14.47 3.54 20.12 8.98c.54.52.17 1.44-.59 1.44z" />
      <path d="m208.46 89.52h-56.92c-1.12 0-1.84-1.19-1.33-2.18 1.11-2.14 2.35-4.21 3.72-6.17.91-1.3 2.39-2.07 3.98-2.07h44.2c1.59 0 3.08.78 3.98 2.08 1.36 1.96 2.6 4.02 3.7 6.16.51.99-.21 2.18-1.33 2.18z" />
      <path d="m213.61 103.47h-67.22c-.98 0-1.69-.92-1.45-1.86.57-2.2 1.15-4.28 1.76-6.11.48-1.46 1.85-2.45 3.4-2.45h59.81c1.54 0 2.91.99 3.4 2.45.61 1.83 1.2 3.91 1.76 6.11.24.94-.48 1.86-1.45 1.86z" />
      <path d="m216.64 117.43h-73.27c-.94 0-1.64-.85-1.47-1.77.36-1.89.74-3.85 1.15-5.81.35-1.65 1.81-2.84 3.5-2.84h66.91c1.7 0 3.16 1.19 3.5 2.84.41 1.96.79 3.91 1.15 5.81.17.92-.53 1.77-1.47 1.77z" />
      <path d="m186.97 131.38c-.83 0-1.5-.67-1.5-1.49 0-1.51-.61-2.87-1.6-3.86s-2.36-1.6-3.87-1.6c-3.02 0-5.47 2.44-5.47 5.45 0 .82-.67 1.49-1.5 1.49h-32.03c-.92 0-1.62-.81-1.48-1.71.2-1.31.51-3.3.91-5.72.29-1.72 1.78-2.98 3.53-2.98h72.09c1.75 0 3.24 1.26 3.53 2.98.4 2.42.71 4.41.91 5.72.14.9-.56 1.71-1.48 1.71h-32.03z" />
    </g>
    <g strokeWidth=".264583" transform="scale(2)">
      <path d="m95.25 39.573729v17.576271h-95.25v-17.576271h28.575l19.05 10.998731 19.05-10.998731z" fill="#aa0200" />
      <path d="m47.625 37.192479.59002 1.820333h1.91294l-1.54781 1.124479.59266 1.817688-1.54781-1.12448-1.54781 1.12448.59002-1.817688-1.54517-1.124479h1.91029z" fill="#fff" />
    </g>
  </svg>
);


/* Government Banner */

const GovBanner = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`tu-govBanner${expanded ? " tu-govBanner--open" : ""}`}>

      <div className="tu-govBanner__strip">
        <div className="tu-govBanner__stripInner">

          <a
            className="tu-govBanner__logoLink"
            href="https://auditor.utah.gov/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Office of Utah State Auditor"
          >
            <img
              src={AuditorLogo}
              alt="Office of Utah State Auditor"
              className="tu-govBanner__logo"
            />
          </a>

          <div className="tu-govBanner__right">
            <UtahFlag />

            <button
              className="tu-govBanner__toggle"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls="tu-govBanner-panel"
            >
              Official Utah government website
              <svg
                className={`tu-govBanner__caret${expanded ? " tu-govBanner__caret--up" : ""}`}
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>

        </div>
      </div>

      <div id="tu-govBanner-panel" className="tu-govBanner__panel" aria-hidden={!expanded}>
        <div className="tu-govBanner__panelInner">

          <div className="tu-govBanner__card">
            <div className="tu-govBanner__cardIcon" aria-hidden="true">
              <svg
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 21h18M3 10h18M12 3l9 7H3l9-7zM5 10v11M19 10v11M9 10v11M15 10v11" />
              </svg>
            </div>
            <div className="tu-govBanner__cardBody">
              <strong>Official Utah websites use utah.gov in the browser's address bar.</strong>
              <p>
                A <strong>.utah.gov</strong> website belongs to an official government
                organization in the State of Utah.
              </p>
            </div>
          </div>

          <div className="tu-govBanner__card">
            <div className="tu-govBanner__cardIcon" aria-hidden="true">
              <svg
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" fill="currentColor" stroke="none" opacity="0.15" />
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </div>
            <div className="tu-govBanner__cardBody">
              <strong>Secure .gov websites use HTTPS</strong>
              <p>
                A lock (
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: 'inline', verticalAlign: 'middle', marginBottom: '1px' }}
                  aria-hidden="true"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                ) or <strong>https://</strong> means you've safely connected to the site.
              </p>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};


/* Main Component */

const FeatureNav = () => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    let lastY = window.scrollY;

    const updateOffset = () => {
      document.documentElement.style.setProperty(
        "--tu-hide-offset",
        `${Math.ceil(nav.getBoundingClientRect().height)}px`
      );
    };

    const onScroll = () => {
      const y = window.scrollY;

      if (y > lastY && y > 10) {
        nav.classList.add("tu-header-hidden");
      }

      if (y < lastY) {
        nav.classList.remove("tu-header-hidden");
      }

      lastY = y;
    };

    updateOffset();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateOffset);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateOffset);
    };
  }, []);

  return (
    <>
      <GovBanner />

      <nav className="tu-megaNav" ref={navRef}>
        <div className="tu-megaNav__inner">
          <a className="tu-megaNav__brand" href="/">
            <img src={Logo} alt="Transparent Utah" height="60" />
          </a>

          <button
            className="tu-megaNav__mobileBtn"
            onClick={() => { setMobileOpen(!mobileOpen); setOpenMenu(null); }}
            aria-label="Toggle navigation"
          >
            <Hamburger />
          </button>

          <ul className={`tu-megaNav__links${mobileOpen ? " tu-megaNav__links--open" : ""}`}>
            {Object.entries(navData).map(([key, menu]) => (
              <li key={key} className="tu-megaNav__item">
                <button
                  className={`tu-megaNav__trigger${openMenu === key ? " tu-megaNav__trigger--active" : ""}`}
                  onClick={() => setOpenMenu(openMenu === key ? null : key)}
                >
                  {menu.label}
                  <Chevron />
                </button>
                {openMenu === key && <MegaPanel sections={menu.sections} />}
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </>
  );
};

export default FeatureNav;